import propz from 'propz';
import { SchoolEvent } from 'Src/models/event';
import { EventInvitationMessage } from 'Src/models/message';
import { getEventNameForParent } from 'Src/helpers/accessor/accessor';
import { AppUser } from 'Src/views/App/App';
import { getChildrenEvents } from 'Src/helpers/service/parent/childrenEvents';

export type ParentConsentEventItem = {
  consentRequest: EventInvitationMessage;
  childEvent: SchoolEvent;
};

export const hydrateParentConsentEvents = async (
  user: AppUser,
  items: ParentConsentEventItem[]
): Promise<ParentConsentEventItem[]> => {
  const eventIds = Array.from(
    new Set(
      items
        .map(item =>
          String(
            item.consentRequest.eventId ||
              propz.get(item.consentRequest, ['eventData', 'id'], '') ||
              propz.get(item.consentRequest, ['eventData', '_id'], '')
          )
        )
        .filter(Boolean)
    )
  );
  const childIdList = Array.from(
    new Set(
      items
        .map(item =>
          String(
            propz.get(item.consentRequest, ['playerDetails', 'userId'], '') ||
              propz.get(item.consentRequest, ['playerDetailsData', 'id'], '')
          )
        )
        .filter(Boolean)
    )
  );
  if (eventIds.length === 0) return items;
  const loadedEvents = await getChildrenEvents(user, {
    limit: eventIds.length,
    skip: 0,
    where: { id: { $in: eventIds }, childIdList }
  }).catch(() => []);
  const eventById = new Map(
    loadedEvents
      .filter(Boolean)
      .map(event => [String(event.id || event._id), event as SchoolEvent])
  );

  return items.map(item => {
    const eventId = String(
      item.consentRequest.eventId || item.childEvent.id || propz.get(item.childEvent, ['_id'], '')
    );
    return { ...item, childEvent: eventById.get(eventId) || item.childEvent };
  });
};

export const getParentConsentEventName = (
  event: SchoolEvent,
  childIdList: string[],
  request?: EventInvitationMessage
): string => {
  const shortDescription =
    propz.get(event, ['eventDetails', 'shortDescription'], '') ||
    propz.get(request, ['eventData', 'eventDetails', 'shortDescription'], '');
  const generatedName = getEventNameForParent(event, childIdList);
  const officialName =
    propz.get(event, ['generatedNames', 'official'], '') ||
    propz.get(request, ['eventData', 'generatedNames', 'official'], '');
  const explicitName =
    propz.get(event, ['name'], '') ||
    propz.get(event, ['title'], '') ||
    propz.get(request, ['eventData', 'name'], '') ||
    propz.get(request, ['eventData', 'title'], '');
  const sportName =
    propz.get(event, ['sport', 'name'], '') || propz.get(request, ['eventData', 'sport', 'name'], '');

  return shortDescription || generatedName || officialName || explicitName || (sportName ? `${sportName} fixture` : 'Untitled event');
};
